import frLocale from "@fullcalendar/core/locales/fr";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Modal } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaRegTrashAlt } from "react-icons/fa";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import CustomModal from "shared/components/Modal";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { USERS } from "utils/api/graphql/queries/users";
import { DATE_TIME_FORMAT, EVENT_TYPES, USER_TYPES } from "utils/constants";
import Detail from "./Detail";

function List({
  data = [],
  addEvent,
  updateEvent,
  deleteEvent,
  user,
  setFilter,
}) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.REMINDER);
  const LIST = t("LIST", { returnObjects: true });
  const DELETE = t("DELETE", { returnObjects: true });
  const DETAIL = t("DETAIL", { returnObjects: true });

  const [modal, setModal] = useState(false);
  const [eventModal, setEventModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState({});
  const [event, setEvent] = useState({});

  function renderEventContent(eventInfo) {
    return (
      <>
        <i>{eventInfo.event.title || LIST.NO_TITLE}</i>
      </>
    );
  }

  const handleDateSelect = (selectInfo) => {
    setModal(true);
    setCurrentEvent({
      start: get(selectInfo, "startStr"),
      end: get(selectInfo, "endStr"),
      allDay: get(selectInfo, "allDay"),
    });
  };

  const handleEventClick = (clickInfo) => {
    setEventModal(true);
    setEvent(get(clickInfo, "event"));
  };

  const handleEventDelete = () => {
    deleteEvent({ variables: { where: { id: get(event, "id") } } });
    setEventModal(false);
    setDeleteModal(false);
  };

  const handleNoClick = () => {
    setEventModal(false);
    setDeleteModal(false);
  };

  const handleEventChange = (changeInfo) => {
    console.log("Event changed:", changeInfo.event);
  };

  const handleAddEvent = () => {
    const startDate = dayjs(currentEvent.start).isValid()
      ? dayjs(currentEvent.start).second(0).millisecond(0)
      : currentEvent.start;
    const endDate = dayjs(currentEvent.end).isValid()
      ? dayjs(currentEvent.end).second(0).millisecond(0)
      : currentEvent.end;
    const startDateStr =
      startDate && startDate.toISOString
        ? startDate.toISOString()
        : startDate;
    const endDateStr =
      endDate && endDate.toISOString ? endDate.toISOString() : endDate;

    addEvent({
      variables: {
        data: {
          id: cuid(),
          startDate: startDateStr,
          endDate: endDateStr,
          type: currentEvent.type,
          allDay: currentEvent.allDay,
          reminder: {
            id: cuid(),
            scheduledDate: startDateStr,
            content: currentEvent.title || "",
            user: {
              id: get(currentEvent, "user.id") || get(user, "id"),
            },
          },
        },
      },
      onCompleted: () => onCancel(),
    });
  };

  const onCancel = () => {
    setCurrentEvent((_) => ({}));
    setModal(false);
  };

  return (
    <div className="grid--1">
      <Header title={LIST.TITLE} />
      <div className="reminder-list-header">
        <Button onClick={() => setModal(true)}>{LIST.ADD_EVENT}</Button>
        <div className="reminder-list-header__filter">
          <Select
            theme="light"
            options={[{ label: "Tout", value: null }].concat(
              Object.entries(EVENT_TYPES).map(([key, value]) => ({
                label: LIST.EVENT_TYPES[key],
                value,
              })),
            )}
            placeholder={LIST.REMINDER_TYPE}
            onChange={(type) => {
              setFilter((prev) => {
                const where = { ...prev.where };
                if (type === null) delete where.type;
                else where.type = type;
                return {
                  ...prev,
                  where,
                };
              });
            }}
          />
        </div>
      </div>

      <FullCalendar
        key={"calendar"}
        editable={false}
        droppable={false}
        eventStartEditable={false}
        eventDurationEditable={false}
        // scrollTime={moment().format(TIME_FORMAT)}
        locale={frLocale}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={{
          left: "prev,today,next",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        buttonText={{
          today: LIST.TODAY,
          month: LIST.MONTH,
          week: LIST.WEEK,
          day: LIST.DAY,
          list: LIST.LIST,
        }}
        initialView={"timeGridWeek"}
        weekends={true}
        events={data.map(
          ({ id, allDay, type, startDate: start, endDate: end, reminder }) => ({
            id,
            title: get(reminder, "content", LIST.NO_TITLE),
            start,
            end,
            allDay,
            meta: { user: get(reminder, "user"), type },
          }),
        )}
        eventContent={renderEventContent}
        selectable={true}
        select={handleDateSelect}
        eventClick={handleEventClick}
        eventChange={handleEventChange}
        // dateClick={handleDateClick}
        eventAdd={(e) => {
          console.log("eventAdd", e);
        }}
        eventRemove={(e) => {
          console.log("eventRemove", e);
        }}
        datesSet={(arg) => {
          setFilter((prev) => ({
            ...prev,
            gt: { startDate: arg.start },
            lt: { startDate: arg.end },
          }));
        }}
      />
      <Modal
        mode="simple"
        open={modal}
        onCancel={onCancel}
        onOk={handleAddEvent}
        title={LIST.ADD_EVENT}
      >
        <div className="grid--1">
          <Input
            placeholder={LIST.EVENT_TITLE}
            label={LIST.EVENT_TITLE}
            value={get(currentEvent, "title")}
            onChange={({ target: { value } }) =>
              setCurrentEvent((prev) => ({ ...prev, title: value }))
            }
          />
          <Input
            type="date"
            variant="range"
            label={LIST.DATES}
            showTime
            format={DATE_TIME_FORMAT}
            disabledDate={(current) => current < dayjs().startOf("day")}
            value={[
              dayjs(get(currentEvent, "start")),
              dayjs(get(currentEvent, "end")),
            ]}
            onChange={([start, end]) =>
              setCurrentEvent((prev) => ({ ...prev, start, end }))
            }
          />
          <Select
            options={Object.entries(EVENT_TYPES).map(([key, value]) => ({
              label: LIST.EVENT_TYPES[key],
              value,
            }))}
            placeholder={LIST.REMINDER_TYPE}
            label={LIST.REMINDER_TYPE}
            value={get(currentEvent, "type")}
            onChange={(value) =>
              setCurrentEvent((prev) => ({ ...prev, type: value }))
            }
          />
          <Select
            type="async"
            node={{
              query: USERS,
              variables: {
                take: null,
                exclude: ["telecoms", "attachments", "broker", "contact"],
                where: {
                  AND: [
                    { isActive: true },
                    {
                      OR: [
                        {
                          meta: { type: USER_TYPES.INTERNAL_ADVISOR.type },
                        },
                        {
                          meta: {
                            type: USER_TYPES.INTERNAL_SUPERVISOR.type,
                          },
                        },
                        { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
                        { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
                        {
                          meta: {
                            type: USER_TYPES.BROKER_REPRESENTATIVE.type,
                          },
                        },
                      ],
                    },
                  ],
                },
              },
              accessors: {
                root: "users.data",
                label: {
                  separator: " ",
                  keys: ["firstname", "lastname"],
                },
              },
            }}
            placeholder={LIST.RECIPIENT}
            label={LIST.RECIPIENT}
            value={get(currentEvent, "user.id")}
            onChange={(value) => {
              setCurrentEvent((prev) => ({ ...prev, user: { id: value } }));
            }}
          />
        </div>
      </Modal>
      <Modal
        open={eventModal}
        title={DETAIL.HEADER}
        footer={
          <div className="d-flex justify--end">
            <Button type="primary--outlined" onClick={() => handleNoClick()}>
              {DETAIL.CLOSE}
            </Button>
            <Button
              type="danger"
              onClick={() => setDeleteModal(true)}
              icon={<FaRegTrashAlt />}
            >
              {DETAIL.DELETE}
            </Button>
          </div>
        }
      >
        <Detail
          event={event}
          translation={DETAIL}
          EVENT_TYPES={LIST.EVENT_TYPES}
        />
      </Modal>
      <CustomModal
        okText={DELETE.OK}
        cancelText={DELETE.CANCEL}
        onCancel={() => setDeleteModal(false)}
        onOk={handleEventDelete}
        message={DELETE.MESSAGE}
        open={deleteModal}
      />
    </div>
  );
}

export default List;
